import type {
  BuzonActivationResult,
  BuzonActivarOpciones,
  BuzonDeactivatePayload,
  BuzonReactivatePayload,
  BuzonBatchDeactivatePayload,
  BuzonInstanciasDisponiblesResponse,
  BuzonNotificationPageResponse,
  BuzonNucPageParams,
  BuzonNucPageResponse,
  BuzonNotificationPageParams,
  BuzonNucIdItem,
  BitacoraAccionRequest,
  BuzonNoLeidasResponse,
} from '@/shared/types/interfaces/BuzonJudicial.interface';
import buzonJudicialUrls from '../utils/ApiUrl/ApiBuzonJudicial';
import { HTTP } from './Http.service';

const http = HTTP();

export const BUZON_JUDICIAL_UPDATED_EVENT = 'buzon-judicial:updated';
/** Obtiene el estado del buzón judicial para un usuario específico */
function getNucBuzonJudicialState(idUsuario: number, searchTerm: string, currentPage: number = 1, pageSize: number = 10, idRelUsuarioEntidades: number | null): Promise<BuzonNucPageResponse> {
  
  const urlToGet = idRelUsuarioEntidades 
    ? `${buzonJudicialUrls.GET_BUZON_NUC_PREFERENCE}?idUsuario=${idUsuario}&idRelUsuarioEntidades=${idRelUsuarioEntidades}&parametro=${searchTerm}&currentPage=${currentPage}&pageSize=${pageSize}`
    : `${buzonJudicialUrls.GET_BUZON_NUC_PREFERENCE}?idUsuario=${idUsuario}&parametro=${searchTerm}&currentPage=${currentPage}&pageSize=${pageSize}`;
  
  return http.Get(`${urlToGet}`);
}
/** Obtiene las notificaciones del buzón judicial para un usuario específico */
function getNotificationsBuzonJudicialState(params: BuzonNotificationPageParams/*idUsuario: number, currentPage: number = 1, pageSize: number = 10, tipoNotificacion?: string*/): Promise<BuzonNotificationPageResponse> {
  const { idUsuario, currentPage = 1, pageSize = 10, tipoNotificacion, searchTerm, idRelUsuarioEntidades } = params;
  let url = `${buzonJudicialUrls.GET_BUZON_NUC_NOTIFICATIONS}?idUsuario=${idUsuario}&currentPage=${currentPage}&pageSize=${pageSize}&parametro=${searchTerm?.trim() ?? ''}`;
  if (tipoNotificacion) url += `&tipoNotificacion=${tipoNotificacion}`;
  if(idRelUsuarioEntidades) url += `&idRelUsuarioEntidades=${idRelUsuarioEntidades}`;
  return http.Get(url);
}
/** Verifica si un usuario puede acceder al módulo del buzón judicial */
export const canAccessBuzonJudicialModule = (totalNUCActivos: number): boolean => {
  return totalNUCActivos > 0;
};

export const getNucPreferencesPage = (params: BuzonNucPageParams): Promise<BuzonNucPageResponse> => {
  return getNucBuzonJudicialState(params.idUsuario, params.searchTerm ?? '', params.pageNumber, params.pageSize, params.idRelUsuarioEntidades);
};

export const getNotificationsPage = (params: BuzonNotificationPageParams): Promise<BuzonNotificationPageResponse> => {
  let { idUsuario, pageSize = 10, tipoNotificacion, searchTerm, idRelUsuarioEntidades } = params;
  // Los consumidores envían `pageNumber` (igual que la paginación de NUCs); el endpoint espera `currentPage`
  const currentPage = params.currentPage ?? params.pageNumber ?? 1;
  tipoNotificacion = params.tipoNotificacion === 'TODAS' ? undefined : params.tipoNotificacion;
  return getNotificationsBuzonJudicialState({ idUsuario, currentPage, pageSize, tipoNotificacion, searchTerm, idRelUsuarioEntidades });
};

// ─── Mutaciones — Activar / Desactivar NUCs ────────────────────
/**
 * Activa uno o más NUCs por sus ids de caso.
 * `opciones` es retrocompatible: sin especificarla, el comportamiento es idéntico al actual (tipoHabilitacion=NUC).
 */
export const activateNucsByIds = async (
  idsCasos: number[],
  idUsuario: number,
  idRelUsuarioEntidades: number | null,
  opciones?: BuzonActivarOpciones,
): Promise<BuzonActivationResult> => {
  const body = { idsCasos, idUsuario, idRelUsuarioEntidades, ...opciones };
  return http.Post(buzonJudicialUrls.ACTIVATE_NUCS, { body });
};

/**
 * Activa un solo NUC — delegado a la versión batch.
 */
export const activateNucById = async (idCaso: number, idUsuario: number, idRelUsuarioEntidades: number | null): Promise<BuzonActivationResult> => {
  return activateNucsByIds([idCaso], idUsuario, idRelUsuarioEntidades);
};

/**
 * Desactiva uno o más NUCs por sus ids de caso.
 */
export const deactivateNucsByIds = async (
  idsCasoBuzon: number[],
  idUsuario: number,
  payload: BuzonDeactivatePayload,
  idRelUsuarioEntidades: number | null,
): Promise<void> => {
  const body = { idsCasoBuzon, idUsuario, idRelUsuarioEntidades, ...payload };
  return http.Post(buzonJudicialUrls.DEACTIVATE_NUCS, { body });
};

/**
 * Desactiva un solo NUC — delegado a la versión batch.
 */
export const deactivateNucById = async (idCasoBuzon: number, idUsuario: number, payload: BuzonDeactivatePayload, idRelUsuarioEntidades: number | null): Promise<void> => {
  return deactivateNucsByIds([idCasoBuzon], idUsuario, payload, idRelUsuarioEntidades);
};

/**
 * Cancela una solicitud pendiente (firma o deshabilitación) para uno o más NUCs.
 */
export const cancelSolicitud = async (idsCasoBuzon: number[], idUsuario: number, idRelUsuarioEntidades: number | null): Promise<void> => {
  const body = { idsCasoBuzon, idUsuario, idRelUsuarioEntidades };
  return http.Post(buzonJudicialUrls.CANCEL_SOLICITUD, { body });
};

/**
 * Obtiene todos los IDs de NUCs del usuario (sin paginación) para selección cross-page.
 */
export const getAllNucIds = async (idUsuario: number, parametro?: string): Promise<BuzonNucIdItem[]> => {
  let url = `${buzonJudicialUrls.GET_ALL_NUC_IDS}?idUsuario=${idUsuario}`;
  if (parametro) {
    url += `&parametro=${parametro}`;
  }
  const response = await http.Get(url);
  return response?.data || [];
};

/**
 * Obtiene las notificaciones judiciales no leídas por el usuario: el total (`count`)
 * y su desglose por tipo (`indicadores`).
 */
export const getUnreadNotifications = async (idUsuario: number, idRelUsuarioEntidades: number | null): Promise<BuzonNoLeidasResponse> => {

  let urlToGet = `${buzonJudicialUrls.GET_UNREAD_COUNT}?idUsuario=${idUsuario}`;

  if(idRelUsuarioEntidades) urlToGet += `&idRelUsuarioEntidades=${idRelUsuarioEntidades}`;

  const response: BuzonNoLeidasResponse | null = await http.Get(urlToGet);
  return { count: response?.count ?? 0, indicadores: response?.indicadores };
};

/**
 * Registra una acción genérica sobre una notificación (fire-and-forget).
 */
export const registrarAccion = async (payload: BitacoraAccionRequest): Promise<void> => {
  return http.Post(buzonJudicialUrls.REGISTRAR_ACCION, { body: payload });
};

/**
 * Registra la visualización de una notificación en la bitácora (fire-and-forget).
 */
export const registrarVisualizacion = async (
  idUsuario: number,
  idTramite: number,
  idSolicitudNotificacion: number,
): Promise<void> => {
  return http.Post(buzonJudicialUrls.REGISTRAR_VISUALIZACION, {
    body: { idUsuario, idTramite, idSolicitudNotificacion },
  });
};

/**
 * Obtiene las categorías de tribunal (instancias) disponibles para uno o varios casos.
 */
export const getInstanciasDisponibles = async (idsCasos: number[]): Promise<BuzonInstanciasDisponiblesResponse> => {
  const params = new URLSearchParams();
  idsCasos.forEach((idCaso) => params.append('idsCasos', String(idCaso)));
  const response = await http.Get(`${buzonJudicialUrls.GET_INSTANCIAS_DISPONIBLES}?${params.toString()}`);
  return response?.data ?? { instanciasDisponibles: [], detallePorCaso: [] };
};

/**
 * Solicita la habilitación de NUCs previamente deshabilitados.
 */
export const reactivateNucsByIds = async (
  idsCasoBuzon: number[],
  idUsuario: number,
  payload: BuzonReactivatePayload,
  idRelUsuarioEntidades: number | null
): Promise<{ success: boolean; idSolicitud: number; message: string }> => {
  const body = { idsCasoBuzon, idUsuario, idRelUsuarioEntidades, ...payload };
  return http.Post(buzonJudicialUrls.REACTIVATE_NUCS, { body });
};
